import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import RefreshIcon from "@mui/icons-material/Refresh";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";
import { adminClient } from "services/apiClient";

function AdminManagement() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [formValues, setFormValues] = useState({
    email: "",
    role: "admin",
  });
  const [creatingAdmin, setCreatingAdmin] = useState(false);
  const [openCreate, setOpenCreate] = useState(false);

  const fetchAdmins = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await adminClient.get("/admin/users");
      setAdmins(response.data || []);
    } catch (err) {
      setError(err.response?.data?.message || "Admin listesi alınamadı.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, []);

  const adminUsers = useMemo(
    () => admins.filter((user) => user.role === "admin" || user.role === "superadmin"),
    [admins]
  );

  const adminColumns = [
    { field: "id", headerName: "User ID", width: 120, sortable: true },
    { field: "email", headerName: "E-posta", flex: 1, minWidth: 220, sortable: true },
    { field: "role", headerName: "Rol", width: 160, sortable: true },
    {
      field: "status",
      headerName: "Durum",
      width: 160,
      sortable: true,
      renderCell: (params) => (
        <Box sx={{ textTransform: "capitalize" }}>{params.value || "-"}</Box>
      ),
    },
  ];
  const filteredAdmins = adminUsers.filter((u) => {
    const matchEmail = (u.email || "").toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = statusFilter === "all" || (u.status || "").toLowerCase() === statusFilter;
    return matchEmail && matchStatus;
  });

  const handleCreateAdmin = async (event) => {
    event.preventDefault();
    setCreatingAdmin(true);
    setError("");
    setSuccess("");

    try {
      await adminClient.post("/admin/user", formValues);
      setSuccess("Admin kullanıcısı başarıyla oluşturuldu.");
      setFormValues({ email: "", role: "admin" });
      setOpenCreate(false);
      fetchAdmins();
    } catch (err) {
      setError(err.response?.data?.message || "Admin oluşturulamadı.");
    } finally {
      setCreatingAdmin(false);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <DashboardLayout>
      <Stack spacing={3} mb={3}>
        <Typography variant="h4" fontWeight="bold">
          Admin Yönetimi
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Superadmin olarak yeni admin hesapları oluşturabilir ve mevcut adminleri görüntüleyebilirsiniz.
        </Typography>
      </Stack>

      {error && (
        <Box mb={3}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}
      {success && (
        <Box mb={3}>
          <Alert severity="success">{success}</Alert>
        </Box>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={12}>
          <Card>
            <CardContent>
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} justifyContent="space-between">
                  <Typography variant="h6">Mevcut Adminler</Typography>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
                    <TextField
                      label="E-posta ile ara"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      size="small"
                      sx={{ minWidth: { xs: '100%', md: 240 }, maxWidth: 300, flexShrink: 0 }}
                    />
                    <Button
                      variant="outlined"
                      startIcon={<RefreshIcon />}
                      onClick={fetchAdmins}
                      disabled={loading}
                      sx={{
                        flexShrink: 0,
                        color: '#000',
                        '&:hover': { borderColor: 'inherit' }
                      }}
                    >
                      Yenile
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => setOpenCreate(true)}
                      sx={{ color: '#fff' }}
                    >
                      Yeni Admin
                    </Button>
                  </Stack>
                </Stack>
                <Box sx={{ height: 520, width: '100%' }}>
                  <DataGrid
                    rows={filteredAdmins}
                    columns={adminColumns}
                    getRowId={(row) => row.id}
                    loading={loading}
                    disableSelectionOnClick
                    sortingOrder={["desc", "asc"]}
                    initialState={{
                      pagination: { paginationModel: { pageSize: 25, page: 0 } },
                      sorting: { sortModel: [{ field: 'id', sort: 'desc' }] },
                    }}
                    pageSizeOptions={[10, 25, 50, 100]}
                    disableColumnSelector
                    disableColumnMenu
                    localeText={{ noRowsLabel: "Admin kullanıcısı bulunmuyor." }}
                    sx={{
                      '& .MuiDataGrid-cell:focus': { outline: 'none' },
                      '& .MuiDataGrid-cell:focus-within': { outline: 'none' },
                      '& .MuiDataGrid-row:hover': { backgroundColor: '#f5f5f5' }
                    }}
                  />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog open={openCreate} onClose={() => setOpenCreate(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Yeni Admin Oluştur</DialogTitle>
        <Box component="form" onSubmit={handleCreateAdmin}>
          <DialogContent dividers>
            <Stack spacing={2}>
              <TextField
                label="Admin E-posta"
                name="email"
                type="email"
                value={formValues.email}
                onChange={handleChange}
                required
                fullWidth
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenCreate(false)}>Vazgeç</Button>
            <Button variant="contained" type="submit" disabled={creatingAdmin} sx={{ color: '#fff' }}>
              {creatingAdmin ? "Oluşturuluyor..." : "Oluştur"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default AdminManagement;
